import {
  Box,
  Container,
  Heading,
  UnorderedList,
  ListItem,
  Text,
} from "@chakra-ui/react";

function Terms() {
  return (
    <Container p={[4, null, 8, 12]} maxW={["fit-content", "container.lg"]}>
      <Box sx={{ "& > *": { paddingBottom: "1rem" } }}>
        <Heading size="lg" as="h2">
          WhalesAI.org Terms of Service
        </Heading>

        <Text>LAST UPDATED: September 5, 2024</Text>
        <Text>By using WhalesAI.org, you agree to these Terms of Service</Text>
        <Text>
          The Nature Conservancy (“we”, “us” or “TNC”) is pleased to provide
          WhalesAI.org software and services (“WhalesAI.org”) to you or your
          current employees. If you are using WhalesAI.org on behalf of a
          business (such as your employer), that business accepts these terms of
          service (“Terms”) by your use. In that case, the words “you” and
          “your” in these Terms refer to that business.
        </Text>
        <Text>
          The Terms govern your access to and use of WhalesAI.org, so please
          carefully read them and our{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="http://www.nature.org/about-us/governance/privacy-policy.xml">
              Privacy Policy
            </a>
          </Text>{" "}
          before using WhalesAI.org. By using WhalesAI.org, you agree to be
          bound by these Terms and by our Privacy Policy. If you don&apos;t
          agree with these Terms and our Privacy Policy, you cannot use
          WhalesAI.org in any way or at any time.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Your Data: Your Rights and Your Privacy
        </Heading>

        <Text>
          We developed WhalesAI.org to help automate the detection of whales
          from satellite imagery. WhalesAI.org allows you to share information
          with us and other users of WhalesAI.org. You agree to allow the
          WhalesAI.org platform to host that information. You further agree to
          share the annotations and labels generated by these detection models
          with TNC and other users of WhalesAI.org and grant TNC permission to
          use those annotations and labels for any purpose. Please note that the
          WhalesAI.org platform does not allow you to share any of the imagery
          and raw image metadata that you upload to WhalesAI.org via
          WhalesAI.org (your “Data”). You retain full ownership of your Data. We
          do not claim any ownership of any of it. These Terms do not grant us
          any right to your Data or intellectual property except for the limited
          rights that we require to administer WhalesAI.org, as explained below.
        </Text>
        <Text>
          Please refer to our{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="http://www.nature.org/about-us/governance/privacy-policy.xml">
              Privacy Policy
            </a>
          </Text>{" "}
          for a description of how we collect, use and disclose WhalesAI.org
          information (“Content”), including your Data.
        </Text>
        <Text>
          We have the right to withdraw or change WhalesAI.org or any Content.
          As we describe below, we will not be liable to you if for any reason
          all or any part of WhalesAI.org is unavailable at any time or for any
          length of time.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Your Responsibilities
        </Heading>

        <Text>
          Content in WhalesAI.org may be protected by intellectual property
          rights of others. Only upload your Data if you have all necessary
          rights to that Data and have all necessary rights to share it on
          WhalesAI.org if you choose to do so. When users upload licensed
          imagery to WhalesAI.org, they are expected to comply with the
          licensor&apos;s terms. Please respect confidentiality obligations. You
          are solely responsible for your conduct, the content of your Data and
          your communications with others while using WhalesAI.org. It is your
          responsibility to ensure that you have the rights or permission needed
          to comply with these Terms.
        </Text>
        <Text>
          Respect the law. Do not use WhalesAI.org for any fraudulent or
          unlawful purpose including violations of statutory, regulatory or
          contractual law. Respect copyright, trademark, privacy, financial
          disclosure, libel, defamation, trade secret, data protection and all
          other laws. Obtain permission before uploading material belonging to
          others. Do not attempt to impersonate any other individual on
          WhalesAI.org; you must always use your true identity.
        </Text>
        <Text>
          TNC expects itself and everyone with whom it does business to conduct
          themselves in ways that are consistent with its TNC&apos;s Code of
          Conduct found at{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="http://www.nature.org/codeofconduct">
              www.nature.org/codeofconduct
            </a>
          </Text>
          .
        </Text>
        <Text>
          Without TNC&apos;s express written consent, you may not use, display,
          mirror, or frame WhalesAI.org, any element within WhalesAI.org, or any
          proprietary elements belonging to TNC (including but not limited to
          TNC logos and marks). In addition to adhering to all applicable laws
          and regulations, you may NOT do any of the following:
        </Text>
        <UnorderedList>
          <ListItem>
            resell WhalesAI.org or the Content, or any portion thereof;
          </ListItem>

          <ListItem>
            access or search the Content or download Content by using any
            engine, software, tool, agent, device or mechanism (including
            spiders, robots, crawlers, data mining tools or the like) other than
            the software and/or search agents that TNC provides or authorizes;
          </ListItem>

          <ListItem>
            probe, scan, tamper with, or test the vulnerability of WhalesAI.org
            or any TNC system or network;
          </ListItem>

          <ListItem>
            avoid, breach, deactivate, impair, or circumvent any security or
            authentication measures, including those that protect WhalesAI.org
            and the Content;
          </ListItem>

          <ListItem>
            attempt to decipher, decompile, disassemble or reverse engineer any
            of the software used to provide WhalesAI.org or the Content;
          </ListItem>

          <ListItem>
            interfere with, or attempt to interfere with, the access of any
            user, host or network, including, without limitation, by sending a
            virus, overloading, or flooding WhalesAI.org;
          </ListItem>

          <ListItem>
            encourage or enable anyone else to do any of the foregoing.
          </ListItem>
        </UnorderedList>
        <Text>
          Although we&apos;re not obligated to monitor and control access to or
          use of WhalesAI.org or to review or edit any Content, we have the
          right to do so to operate WhalesAI.org, ensure compliance with these
          Terms, and comply with applicable legal requirements. We have the
          right to investigate violations of these Terms or conduct that affects
          the Services. We may also consult and cooperate with law enforcement
          authorities to prosecute users who violate the law.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Content
        </Heading>

        <Text>
          Using WhalesAI.org or uploading your Data does not give you ownership
          of any intellectual property rights in WhalesAI.org or in Content
          (other than your own Data) that you may access or use. TNC and its
          licensors exclusively own all right, title and interest in and to
          WhalesAI.org, including all associated intellectual property rights.
          Don&apos;t remove, obscure, or alter any legal notices displayed in or
          along with WhalesAI.org.
        </Text>
        <Text>
          All Users are encouraged to make appropriate attributions to copyright
          owners when referencing their material. We have the right to remove
          content and take technical and legal steps to bar you from using
          WhalesAI.org if we think you have violated these Terms. YOU WAIVE AND
          HOLD HARMLESS TNC AND ITS AFFILIATES AND SERVICE PROVIDERS FROM ANY
          CLAIMS RESULTING FROM ANY UNAUTHORIZED USE OF CONTENT YOU UPLOAD, POST
          OR USE IN WhalesAI.org.
        </Text>
        <Text>
          These Terms do not grant you the right to use any of our branding or
          logos or name. You must not use our name or logo or branding without
          our written permission, except to cite The Nature Conservancy&apos;s
          development and ownership of WhalesAI.org in a factual manner that
          does not imply any endorsement of Your or anyone else&apos;s products,
          services, or businesses
        </Text>
        <Heading size="md" mt="4" as="h3">
          Account Information and Security
        </Heading>

        <Text>
          You will be assigned a username and password to access WhalesAI.org.
          You agree not to use the username or password of another person at any
          time or to disclose your password to any third party. You agree to
          notify us immediately if you suspect any unauthorized use of your
          username or access to your password. You are solely responsible for
          any and all uses of your username and password, as well as all
          activities that occur under your Account, whether or not you know
          about them. You can deactivate your account at any time by sending an
          email to ca.software.whalesai at tnc dot org with “Deactivate my
          Account” in the subject line.
        </Text>
        <Text>
          It&apos;s important that you provide us with accurate, complete and
          up-to-date information for your Account and you agree to update such
          information to keep it accurate and complete. If you don&apos;t, we
          might have to suspend or terminate your Account.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Rights You Grant in Your Data for WhalesAI.org System Administration
        </Heading>

        <Text>
          By uploading your Data, you hereby grant to TNC a non-exclusive,
          transferable, sub licensable, worldwide, royalty-free license to use,
          copy, modify, create derivative works based upon your Data in
          connection with operating and providing WhalesAI.org to you and to
          other WhalesAI.org users.
        </Text>
        <Text>
          You are solely responsible for all your Data. You represent and
          warrant that you own all your Data or that you have all necessary
          rights to grant us the license rights in your Data under these Terms.
          You also represent and warrant that neither your Data, nor your use
          and provision of your Data to be made available through WhalesAI.org,
          nor any use of your Data by TNC on or through the WhalesAI.org will
          infringe, misappropriate, or violate a third party&apos;s intellectual
          property rights, or rights of privacy, or result in the violation of
          any applicable law or regulation.
        </Text>
        <Text>
          You can remove your Data by specifically deleting it. However, in
          certain instances, some of your Data (such as summaries aggregating
          your Data with other Content) may not be completely removed and copies
          of your Data may continue to exist on WhalesAI.org. We are not
          responsible or liable for the removal or deletion of (or the failure
          to remove or delete) any of your Data.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Rights you grant in your data for other purposes
        </Heading>

        <Text>
          TNC will not display or distribute your Data (including using your
          Data to train machine learning models) for purposes other than system
          administration without your consent.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Rights That TNC Grants You
        </Heading>

        <Text>
          The Nature Conservancy is the author and owner of the WhalesAI.org
          code, including all copyrights in WhalesAI.org. We grant you a
          license, free of charge, to use this WhalesAI.org software and
          associated documentation files (the “Software”) for noncommercial or
          academic purposes, including the rights to use, copy, modify, merge,
          publish, or distribute, or sublicense the Software, and to permit
          persons to whom the Software is furnished to do so, subject to the
          following conditions:
        </Text>
        <UnorderedList>
          <ListItem>
            You must include the following copyright notice and this permission
            notice in all copies or substantial portions of the Software: “© The
            Nature Conservancy, 2024. The Nature Conservancy is the author and
            owner of the WhalesAI.org code, version, including all copyrights in
            WhalesAI.org. Only for noncommercial or academic use.”
          </ListItem>
        </UnorderedList>
        <Heading size="md" mt="8" as="h3">
          Disclaimers, Limited Liability and Indemnity
        </Heading>

        <Text>
          THE SOFTWARE IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTY
          OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, QUIET
          ENJOYMENT, AND NON INFRINGEMENT. TNC MAKES NO WARRANTY AS TO THE
          QUALITY, ACCURACY, COMPLETENESS, TIMELINESS, OR RELIABILITY OF
          WhalesAI.org OR ANY WhalesAI.org CONTENT.
        </Text>
        <Text>
          TNC MAKES NO REPRESENTATIONS OR WARRANTIES THAT YOUR USE OF
          WhalesAI.org WILL MEET YOUR NEEDS OR BE UNINTERRUPTED, SECURE, OR
          ERROR FREE. USERS ARE RESPONSIBLE FOR TAKING ALL NECESSARY PRECAUTIONS
          TO ENSURE THAT ANY CONTENT YOU MAY OBTAIN FROM THE SERVICES IS FREE OF
          VIRUSES OR OTHER HARMFUL CODE.
        </Text>
        <Text>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TNC SHALL NOT BE LIABLE FOR
          CREATING, PRODUCING, MAINTAINING, OPERATING, OR PROVIDING WhalesAI.org
          or WhalesAI.org CONTENT, UNDER ANY THEORY BASED IN CONTRACT, WARRANTY,
          TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY, STRICT LIABILITY, OR
          OTHER LEGAL THEORY. THIS LIMITATION ON LIABILITY INCLUDES ANY AND ALL
          INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY OR CONSEQUENTIAL DAMAGES,
          INCLUDING LOST PROFITS, LOSS OF DATA OR GOODWILL, SERVICE
          INTERRUPTION, COMPUTER DAMAGE OR SYSTEM FAILURE OR THE COST OF
          SUBSTITUTE SERVICES ARISING OUT OF OR IN CONNECTION WITH THESE TERMS
          OR FROM THE USE OF OR INABILITY TO USE WhalesAI.org, EVEN IF TNC HAS
          BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </Text>
        <Text>
          THE ABOVE EXCLUSIONS AND LIMITATIONS OF DAMAGES ARE FUNDAMENTAL
          ELEMENTS OF THE BASIS OF THE BARGAIN BETWEEN TNC AND YOU.
        </Text>
        <Text>
          INDEMNITY. You agree to release, indemnify, defend and hold harmless
          TNC, its subsidiaries, and affiliates, and their respective officers,
          directors, agents, partners and employees, from and against any
          claims, disputes, demands, liabilities, damages, losses, and costs and
          expenses, including, without limitation, reasonable legal and
          accounting fees arising out of or in any way connected with (i) your
          access to or use of WhalesAI.org, (ii) your Data, or (iii) your
          violation of these Terms.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Termination
        </Heading>

        <Text>
          We may terminate your access to and use of WhalesAI.org, at our sole
          discretion, at any time and without notice to you. You may cancel your
          Account at any time by sending an email to us at WhalesAI.org at tnc
          dot org. Upon any termination, discontinuation or cancellation of
          WhalesAI.org or your Account, all provisions of these Terms that by
          their nature should survive will survive. This includes, for example
          ownership provisions, warranty disclaimers, and limitations of
          liability.
        </Text>
        <Heading size="md" mt="4" as="h3">
          Modifying our Terms of Service
        </Heading>

        <Text>
          We may update these Terms from time to time, at our sole discretion.
          We may notify you of such updates by any reasonable means, such as by
          posting the revised Terms on the WhalesAI.org website. Please look at
          the “LAST UPDATED” legend above to see when these Terms were last
          revised. Your use of WhalesAI.org after the posting of any revised
          Terms means that you accept and agree to be bound by the revised
          Terms. If you don&apos;t agree to be bound by the revised Terms, then
          you may not use WhalesAI.org anymore. You can stop using WhalesAI.org
          at any time. We may also stop providing WhalesAI.org to you, or add or
          create new limits to WhalesAI.org at any time.
        </Text>
        <Heading size="md" mt="4" as="h3">
          General Terms
        </Heading>

        <Text>
          These Terms are the entire and exclusive agreement between TNC and you
          regarding WhalesAI.org and these Terms supersede and replace any prior
          agreements between TNC and you regarding WhalesAI.org.
        </Text>
        <Text>
          These terms do not create any third party beneficiary rights. You may
          not assign or transfer these Terms, by operation of law or otherwise,
          without TNC&apos;s prior written consent. Any attempt to do so will be
          null. Subject to the foregoing, these Terms will bind your successors
          and permitted assigns. TNC may freely assign or transfer these Terms
          without restriction.
        </Text>
        <Text>
          If you do not comply with these Terms, and we do not take action right
          away, this does not mean that we are giving up any rights that we may
          have to take action in the future or seek all available remedies.
        </Text>
        <Text>
          If it turns out that a particular provision of these Terms is not
          fully valid or enforceable, that provision will be enforced to the
          maximum extent permissible, and this will not affect any other Terms.
        </Text>
        <Text>
          All claims arising out of this Agreement or relating to WhalesAI.org
          will be governed by the laws of the State of California excluding the
          application of its conflicts of law rules. Any legal action or
          proceeding arising out of this Agreement or relating to these Services
          shall be brought exclusively in a state or federal court in or for San
          Francisco, California.
        </Text>
      </Box>
    </Container>
  );
}

export default Terms;
